import React, { useState, useEffect } from 'react';
import { X, Plus, Minus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const ProductModal = ({ product, category, onClose }) => {
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedSide, setSelectedSide] = useState(null);
  const [selectedToppings, setSelectedToppings] = useState([]);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [selectedSpice, setSelectedSpice] = useState(null);
  const [customerNote, setCustomerNote] = useState('');
  const [totalPrice, setTotalPrice] = useState(0);

  useEffect(() => {
    if (product) {
      // Reset state
      setQuantity(1);
      setSelectedToppings([]);
      setSelectedExtras([]);
      setCustomerNote('');
      
      // Set default size
      if (product.sizeOptions && product.sizeOptions.length > 0) {
        setSelectedSize(product.sizeOptions[0].id);
      } else {
        setSelectedSize(null);
      }
      
      // Set default side
      if (product.sideOptions && product.sideOptions.length > 0) {
        setSelectedSide(product.sideOptions[0].id);
      } else {
        setSelectedSide(null);
      }
      
      // Set default spice
      if (product.spiceOptions && product.spiceOptions.length > 0) {
        setSelectedSpice(product.spiceOptions[0].id);
      } else {
        setSelectedSpice(null);
      }
    }
  }, [product]);

  useEffect(() => {
    calculateTotalPrice();
  }, [selectedSize, selectedSide, selectedToppings, selectedExtras, selectedSpice, quantity, product]);

  const calculateTotalPrice = () => {
    if (!product) return;

    let basePrice = product.price || product.basePrice || 0;

    // Size price
    if (selectedSize && product.sizeOptions) {
      const sizeOption = product.sizeOptions.find(s => s.id === selectedSize);
      if (sizeOption) basePrice = sizeOption.price;
    }

    // Side option price
    if (selectedSide && product.sideOptions) {
      const sideOption = product.sideOptions.find(s => s.id === selectedSide);
      if (sideOption && sideOption.price > 0) basePrice += sideOption.price;
    }

    // Spice price
    if (selectedSpice && product.spiceOptions) {
      const spiceOption = product.spiceOptions.find(s => s.id === selectedSpice);
      if (spiceOption) basePrice += spiceOption.price;
    }

    // Toppings price
    const toppingsTotal = selectedToppings.reduce((sum, toppingId) => {
      const topping = product.toppingOptions?.find(t => t.id === toppingId);
      return sum + (topping ? topping.price : 0);
    }, 0);

    // Extras price
    const extrasTotal = selectedExtras.reduce((sum, extraId) => {
      const extra = product.extras?.find(e => e.id === extraId);
      return sum + (extra ? extra.price : 0);
    }, 0);

    setTotalPrice((basePrice + toppingsTotal + extrasTotal) * quantity);
  };

  const handleToppingToggle = (toppingId) => {
    setSelectedToppings(prev => 
      prev.includes(toppingId) 
        ? prev.filter(id => id !== toppingId)
        : [...prev, toppingId]
    );
  };

  const handleExtraToggle = (extraId) => {
    setSelectedExtras(prev => 
      prev.includes(extraId) 
        ? prev.filter(id => id !== extraId)
        : [...prev, extraId]
    );
  };

  const handleAddToCart = () => {
    if (!product) return;

    let productName = product.name;
    let basePrice = product.price || product.basePrice || 0;
    const selectedOptions = [];

    // Size
    if (selectedSize && product.sizeOptions) {
      const sizeOption = product.sizeOptions.find(s => s.id === selectedSize);
      if (sizeOption) {
        productName = `${product.name} (${sizeOption.name})`;
        basePrice = sizeOption.price;
        selectedOptions.push(sizeOption.name);
      }
    }

    // Side
    if (selectedSide && product.sideOptions) {
      const sideOption = product.sideOptions.find(s => s.id === selectedSide);
      if (sideOption) {
        selectedOptions.push(sideOption.name);
      }
    }

    // Toppings
    if (selectedToppings.length > 0 && product.toppingOptions) {
      selectedToppings.forEach(toppingId => {
        const topping = product.toppingOptions.find(t => t.id === toppingId);
        if (topping) selectedOptions.push(topping.name);
      });
    }

    // Spice
    if (selectedSpice && product.spiceOptions) {
      const spiceOption = product.spiceOptions.find(s => s.id === selectedSpice);
      if (spiceOption && spiceOption.price > 0) {
        basePrice += spiceOption.price;
        selectedOptions.push(spiceOption.name);
      } else if (spiceOption && spiceOption.name !== 'Normál') {
        selectedOptions.push(spiceOption.name);
      }
    }

    // Extras
    const extrasNames = [];
    if (selectedExtras.length > 0 && product.extras) {
      selectedExtras.forEach(extraId => {
        const extra = product.extras.find(e => e.id === extraId);
        if (extra) {
          extrasNames.push(extra.name);
          basePrice += extra.price;
        }
      });
    }

    // Calculate final price per item
    const finalPricePerItem = basePrice;

    const cartItem = {
      ...product,
      name: productName,
      price: finalPricePerItem,
      quantity: quantity,
      selectedSize: selectedSize ? product.sizeOptions?.find(s => s.id === selectedSize)?.name : null,
      selectedSide: selectedSide ? product.sideOptions?.find(s => s.id === selectedSide)?.name : null,
      selectedOptions: selectedOptions,
      selectedExtras: extrasNames,
      selectedSpice: selectedSpice ? product.spiceOptions?.find(s => s.id === selectedSpice)?.name : null,
      customerNote: customerNote.trim() || null,
      cartItemId: `${product.id}-${Date.now()}-${Math.random()}`
    };

    addToCart(cartItem);
    toast.success(`${quantity}x ${productName} hozzáadva a kosárhoz!`);
    onClose();
  };

  if (!product) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 animate-fadeIn" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slideUp">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-[#00BCD4] to-[#00ACC1] text-white p-6 rounded-t-3xl z-10">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <h2 className="text-2xl font-black pr-12">{product.name}</h2>
          {product.description && (
            <p className="text-sm opacity-90 mt-2">{product.description}</p>
          )}
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Size Options */}
          {product.sizeOptions && product.sizeOptions.length > 0 && (
            <div>
              <h3 className="font-bold text-gray-900 mb-3">Válassz méretet:</h3>
              <div className="space-y-2">
                {product.sizeOptions.map((size) => (
                  <button
                    key={size.id}
                    onClick={() => setSelectedSize(size.id)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                      selectedSize === size.id
                        ? 'border-[#00BCD4] bg-[#00BCD4]/10'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="font-semibold text-gray-900">{size.name}</span>
                    <span className="font-bold text-[#FFA726]">{size.price} Ft</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Side Options */}
          {product.sideOptions && product.sideOptions.length > 0 && (
            <div>
              <h3 className="font-bold text-gray-900 mb-3">Köret választása:</h3>
              <div className="space-y-2">
                {product.sideOptions.map((side) => (
                  <button
                    key={side.id}
                    onClick={() => setSelectedSide(side.id)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                      selectedSide === side.id
                        ? 'border-[#00BCD4] bg-[#00BCD4]/10'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="font-semibold text-gray-900">{side.name}</span>
                    {side.price > 0 && (
                      <span className="font-bold text-[#FFA726]">+{side.price} Ft</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Topping Options (Checkboxes or Radio) */}
          {product.toppingOptions && product.toppingOptions.length > 0 && (
            <div>
              <h3 className="font-bold text-gray-900 mb-3">
                {product.id.includes('gofri') 
                  ? 'Válassz öntetet:' 
                  : product.id.includes('hotdog') || product.id.includes('kolbasz') 
                  ? 'Választható feltétek:' 
                  : 'Feltétek:'}
              </h3>
              <div className="space-y-2">
                {product.toppingOptions.map((topping) => {
                  const isRadio = topping.type === 'radio';
                  const isSelected = isRadio 
                    ? selectedToppings[0] === topping.id 
                    : selectedToppings.includes(topping.id);
                  
                  return (
                    <label
                      key={topping.id}
                      className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <input
                          type={isRadio ? 'radio' : 'checkbox'}
                          name={isRadio ? 'topping-radio' : undefined}
                          checked={isSelected}
                          onChange={() => {
                            if (isRadio) {
                              setSelectedToppings([topping.id]);
                            } else {
                              handleToppingToggle(topping.id);
                            }
                          }}
                          className="w-5 h-5 text-[#00BCD4] rounded focus:ring-[#00BCD4]"
                        />
                        <span className="font-medium text-gray-900">{topping.name}</span>
                      </div>
                      {topping.price > 0 && (
                        <span className="text-sm font-semibold text-[#FFA726]">+{topping.price} Ft</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Spice Options */}
          {product.spiceOptions && product.spiceOptions.length > 0 && (
            <div>
              <h3 className="font-bold text-gray-900 mb-3">Csípősség:</h3>
              <div className="space-y-2">
                {product.spiceOptions.map((spice) => (
                  <button
                    key={spice.id}
                    onClick={() => setSelectedSpice(spice.id)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                      selectedSpice === spice.id
                        ? 'border-[#FFA726] bg-[#FFA726]/10'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="font-semibold text-gray-900">{spice.name}</span>
                    {spice.price > 0 && (
                      <span className="font-bold text-[#FFA726]">+{spice.price} Ft</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Extras */}
          {product.extras && product.extras.length > 0 && (
            <div>
              <h3 className="font-bold text-gray-900 mb-3">Extrák:</h3>
              <div className="space-y-2">
                {product.extras.map((extra) => (
                  <label
                    key={extra.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={selectedExtras.includes(extra.id)}
                        onChange={() => handleExtraToggle(extra.id)}
                        className="w-5 h-5 text-[#00BCD4] rounded focus:ring-[#00BCD4]"
                      />
                      <span className="font-medium text-gray-900">{extra.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-[#FFA726]">+{extra.price} Ft</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Customer Note Field - for main products */}
          {product.hasNoteField && (
            <div>
              <h3 className="font-bold text-gray-900 mb-3">📝 Megjegyzés:</h3>
              <textarea
                value={customerNote}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 120) {
                    setCustomerNote(value);
                  }
                }}
                placeholder="Például: hagyma nélkül, külön szósz"
                className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-[#00BCD4] focus:outline-none resize-none"
                rows="3"
                maxLength={120}
              />
              <p className="text-xs text-gray-500 mt-2">
                {customerNote.length}/120 karakter
                {customerNote.length >= 100 && customerNote.length < 120 && (
                  <span className="text-orange-600 ml-2">⚠️ Közeledik a limit</span>
                )}
                {customerNote.length === 120 && (
                  <span className="text-red-600 ml-2">⚠️ Maximum elérve</span>
                )}
              </p>
            </div>
          )}

          {/* Quantity Selector */}
          <div>
            <h3 className="font-bold text-gray-900 mb-3">Mennyiség:</h3>
            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                disabled={quantity <= 1}
              >
                <Minus className="w-5 h-5 text-gray-700" />
              </button>
              <span className="text-3xl font-black text-gray-900 w-16 text-center">
                {quantity}
              </span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Plus className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 p-6 rounded-b-3xl border-t border-gray-200">
          <button
            onClick={handleAddToCart}
            className="w-full py-4 bg-gradient-to-r from-[#FFA726] to-[#FF9800] text-white rounded-xl font-bold text-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-between px-6"
          >
            <span>Kosárba ({quantity} db)</span>
            <span className="text-2xl">{totalPrice} Ft</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;
